import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { useAuthStore } from '../../stores/auth.store';
import { isSignupEnabled } from '../../lib/feature-flags';
import logo from '../../assets/images/logo.svg';

export interface LoginFormProps {
  onSubmit?: (data: { email: string; password: string }) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      if (onSubmit) {
        onSubmit({ email, password });
      }
      navigate('/dashboard');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erreur lors de la connexion. Veuillez réessayer.',
      );
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <div className="flex justify-center mb-6">
          <img src={logo} alt="ZuroQ Logo" className="h-12 w-auto" />
        </div>
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">Login</h1>
        <p className="text-center text-gray-600 text-sm mb-8">
          Entrez vos identifiants pour accéder à votre compte
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="email">Adresse email</Label>
            <Input
              type="email"
              id="email"
              placeholder="admin@example.com"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="password">Mot de passe</Label>
            <Input
              type="password"
              id="password"
              placeholder="••••••••"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && <div className="text-red-600 text-sm text-center">{error}</div>}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Connexion...' : 'Se connecter'}
          </Button>
        </form>

        {/* HZVT-150 — Forgot password link routes to /forgot-password (guest-only). */}
        <p className="text-center text-sm text-gray-600 mt-4">
          <Link to="/forgot-password" className="font-medium text-indigo-600 hover:text-indigo-500">
            Mot de passe oublié ?
          </Link>
        </p>

        {isSignupEnabled() && (
          <p className="text-center text-sm text-gray-600 mt-6">
            Pas encore de compte ?{' '}
            <Link to="/signup" className="font-medium text-indigo-600 hover:text-indigo-500">
              S&apos;inscrire
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};
